import { AppState, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { normalizeApiError } from '../api/apiError';
import {
  startLocationForegroundService,
  stopLocationForegroundService,
} from '../native/LocationTrackingService';
import type {
  LocationTrackingSettings,
  TrackingPoint,
  TrackingSessionState,
  TrackingUploadResult,
} from '../types/locationTracking';
import {
  shouldRecordFix,
  toGeoFix,
  toTrackingPoint,
  type GeoFix,
} from '../utils/locationTracking';
import {
  getLocationSource,
  withTimeout,
  type GeoError,
  type LocationSource,
  type PermissionResult,
  type RawPosition,
  type WatchHandle,
} from './locationSources';
import {
  startLocationTracking,
  uploadTrackingPoints,
} from './locationTrackingService';
import {
  createTrackingPointQueue,
  type QueueStorage,
  type TrackingPointQueue,
} from './trackingPointQueue';

export type { PermissionResult } from './locationSources';

export type TrackerState =
  | 'idle'
  | 'starting'
  | 'tracking'
  | 'permission_denied'
  | 'permission_blocked'
  | 'location_disabled'
  | 'unavailable'
  | 'error';

export type TrackerStatus = {
  state: TrackerState;
  attendanceId: number | null;
  pendingPoints: number;
  lastFixAt: string | null;
  lastUploadAt: string | null;
  /** Upload failed for a network reason; points stay queued on the device. */
  offline: boolean;
  /** Android foreground service / iOS background updates are running. */
  background: boolean;
  /** Why tracking could not start, for the 'error' state. */
  error: string | null;
};

export type TrackerDependencies = {
  platform: string;
  source: () => LocationSource;
  startSession: (
    attendanceId: number,
    fix?: { latitude: number; longitude: number; accuracyMeters: number | null } | null,
  ) => Promise<TrackingSessionState>;
  uploadPoints: (
    attendanceId: number,
    points: TrackingPoint[],
  ) => Promise<TrackingUploadResult>;
  storage: QueueStorage;
  onNetworkRestored: (listener: () => void) => () => void;
  onAppStateChange: (listener: (state: string) => void) => () => void;
  currentAppState: () => string;
  startBackground: () => Promise<boolean>;
  stopBackground: () => Promise<void>;
  now: () => number;
  /** Upper bounds for native steps; a step that never answers fails instead of hanging. */
  timeouts?: { permissionMs?: number; watchStartMs?: number };
};

type ActiveRecord = {
  attendanceId: number;
  userId: string | null;
  settings: LocationTrackingSettings | null;
  /** Session ended; only queued points remain to upload. */
  drainOnly?: boolean;
};

const ACTIVE_KEY = 'locationTracking.active';
const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const BACKOFF_BASE_MS = 5000;
// The user may still be reading the system prompt; afterwards treat it as "not allowed yet".
const DEFAULT_PERMISSION_TIMEOUT_MS = 60000;
const DEFAULT_WATCH_START_TIMEOUT_MS = 15000;
// Server answers meaning this attendance will never accept more points.
const TERMINAL_CODES = new Set([
  'LOCATION_TRACKING_NOT_ELIGIBLE',
  'LOCATION_TRACKING_ENDED',
  'LOCATION_TRACKING_UNAVAILABLE',
  'ATTENDANCE_NOT_FOUND',
]);
const START_FAILED = 'Live tracking could not start. Check your connection and try again.';

const IDLE: TrackerStatus = {
  state: 'idle',
  attendanceId: null,
  pendingPoints: 0,
  lastFixAt: null,
  lastUploadAt: null,
  offline: false,
  background: false,
  error: null,
};

function isLocationDisabledError(error: GeoError): boolean {
  return (
    error.code === POSITION_UNAVAILABLE &&
    /disabled|no location provider/i.test(error.message ?? '')
  );
}

function permissionState(permission: PermissionResult, platform: string): TrackerState {
  if (permission === 'disabled') return 'location_disabled';
  if (permission === 'blocked') return 'permission_blocked';
  // iOS can only change a refusal in Settings.
  return platform === 'ios' ? 'permission_blocked' : 'permission_denied';
}

export function createLocationTracker(deps: TrackerDependencies) {
  const permissionMs = deps.timeouts?.permissionMs ?? DEFAULT_PERMISSION_TIMEOUT_MS;
  const watchStartMs = deps.timeouts?.watchStartMs ?? DEFAULT_WATCH_START_TIMEOUT_MS;
  let status: TrackerStatus = { ...IDLE };
  let record: ActiveRecord | null = null;
  let settings: LocationTrackingSettings | null = null;
  let queue: TrackingPointQueue | null = null;
  let lastRecorded: GeoFix | null = null;
  let watch: WatchHandle | null = null;
  let heartbeat: ReturnType<typeof setInterval> | null = null;
  let unsubscribers: (() => void)[] = [];
  let flushing: Promise<void> | null = null;
  let rerun: 'normal' | 'force' | null = null;
  let lastUploadAttemptMs = 0;
  let failures = 0;
  let nextAttemptMs = 0;
  // Guards against a stale async start finishing after stop() or a newer start().
  let generation = 0;
  let pendingStart: { attendanceId: number; promise: Promise<TrackerStatus> } | null = null;
  // Last requested start, so retry() can repeat it after a failure.
  let lastRequest: { attendanceId: number; userId: string | null } | null = null;
  const listeners = new Set<(next: TrackerStatus) => void>();

  function emit(patch: Partial<TrackerStatus>) {
    status = { ...status, ...patch, pendingPoints: queue?.size() ?? 0 };
    listeners.forEach(listener => listener(status));
  }

  async function persistRecord() {
    try {
      if (record) await deps.storage.setItem(ACTIVE_KEY, JSON.stringify(record));
      else await deps.storage.removeItem(ACTIVE_KEY);
    } catch {
      // Best effort.
    }
  }

  async function openQueue(attendanceId: number): Promise<TrackingPointQueue> {
    const next = createTrackingPointQueue(deps.storage, attendanceId);
    await next.load();
    return next;
  }

  function stopSensors() {
    if (watch) {
      try {
        watch.stop();
      } catch {
        // Ignore.
      }
      watch = null;
    }
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = null;
    unsubscribers.forEach(unsubscribe => unsubscribe());
    unsubscribers = [];
    if (status.background) void deps.stopBackground();
  }

  async function clearRecord() {
    await queue?.clear();
    queue = null;
    record = null;
    await persistRecord();
  }

  function onFix(position: unknown) {
    if (!record || record.drainOnly || !settings || !queue) return;
    const fix = toGeoFix(position as Parameters<typeof toGeoFix>[0]);
    if (!fix || !shouldRecordFix(lastRecorded, fix, settings)) return;
    lastRecorded = fix;
    void queue.append(
      toTrackingPoint(fix, record.attendanceId, deps.currentAppState() !== 'active'),
    );
    void queue.trim(settings.maxQueuedPoints);
    // A fix after an error proves location works again.
    emit({
      lastFixAt: new Date(fix.timestamp).toISOString(),
      ...(status.state === 'location_disabled' ? { state: 'tracking' as const } : {}),
    });
    void flush(false);
  }

  function onWatchError(error: GeoError) {
    if (error.code === PERMISSION_DENIED) {
      emit({ state: deps.platform === 'ios' ? 'permission_blocked' : 'permission_denied' });
      return;
    }
    if (isLocationDisabledError(error)) {
      emit({ state: 'location_disabled' });
    }
    // Timeouts and transient "unavailable" fixes: keep watching; the next fix recovers.
  }

  function fail(error: string) {
    emit({ state: 'error', error, background: false });
  }

  async function startSensors(current: number) {
    if (!settings) return;
    if (watch) {
      watch.stop();
      watch = null;
    }
    const source = deps.source();
    const permission = await withTimeout(source.requestPermission(), permissionMs).catch(
      () => 'denied' as const,
    );
    if (current !== generation) return;
    if (permission !== 'granted') {
      emit({ state: permissionState(permission, deps.platform), error: null });
      return;
    }
    let background = false;
    if (deps.platform === 'android') {
      background = await deps.startBackground();
      if (current !== generation) {
        if (background) await deps.stopBackground();
        return;
      }
    }
    try {
      watch = await withTimeout(
        source.watch(onFix, onWatchError, {
          intervalMs: settings.sampleIntervalSeconds * 1000,
          fastestIntervalMs: settings.minSampleIntervalSeconds * 1000,
          highAccuracy: settings.highAccuracy,
        }),
        watchStartMs,
      );
    } catch (caught) {
      if (background) await deps.stopBackground();
      if (current !== generation) return;
      const error = caught as GeoError;
      if (error?.code === PERMISSION_DENIED) {
        emit({ state: permissionState('denied', deps.platform), background: false });
      } else if (isLocationDisabledError(error)) {
        emit({ state: 'location_disabled', background: false });
      } else {
        fail('Location could not be started on this device. Try again.');
      }
      return;
    }
    if (current !== generation) {
      watch.stop();
      watch = null;
      if (background) await deps.stopBackground();
      return;
    }
    background = background || watch.background;
    // Some devices stop reporting while stationary; ask explicitly so stays are measured.
    if (heartbeat) clearInterval(heartbeat);
    const s = settings;
    heartbeat = setInterval(() => {
      const elapsed = deps.now() - (lastRecorded?.timestamp ?? 0);
      if (elapsed >= s.sampleIntervalSeconds * 1000) {
        source
          .currentPosition({
            timeoutMs: s.sampleIntervalSeconds * 1000,
            maximumAgeMs: s.minSampleIntervalSeconds * 1000,
            highAccuracy: s.highAccuracy,
          })
          .then((position: RawPosition) => onFix(position), () => undefined);
      }
      void flush(false);
    }, s.sampleIntervalSeconds * 1000);
    emit({ state: 'tracking', background, error: null });
  }

  function subscribeLifecycle() {
    unsubscribers.forEach(unsubscribe => unsubscribe());
    unsubscribers = [];
    unsubscribers.push(deps.onNetworkRestored(() => void flush(true)));
    unsubscribers.push(
      deps.onAppStateChange(next => {
        if (next !== 'active') return;
        void flush(true);
        // Permission or location services may have been fixed in Settings.
        if (
          record &&
          !record.drainOnly &&
          ['permission_denied', 'permission_blocked', 'location_disabled', 'error'].includes(
            status.state,
          )
        ) {
          void startSensors(generation);
        }
      }),
    );
  }

  async function finishSession() {
    stopSensors();
    if (queue?.size() && record) {
      record = { ...record, drainOnly: true };
      await persistRecord();
      // Keep uploading the remaining points when the connection returns.
      subscribeLifecycle();
      emit({ state: 'idle', background: false });
      return;
    }
    await clearRecord();
    status = { ...IDLE };
    emit({});
  }

  async function uploadLoop(force: boolean) {
    if (!record || !settings || !queue) return;
    const now = deps.now();
    if (!force && now < nextAttemptMs) return;
    if (
      !force &&
      queue.size() < settings.uploadBatchSize &&
      now - lastUploadAttemptMs < settings.uploadIntervalSeconds * 1000
    ) {
      return;
    }
    while (record && queue && queue.size()) {
      const attendanceId = record.attendanceId;
      const batch = queue.peek(settings.uploadBatchSize);
      const sent = new Set(batch.map(point => point.clientPointId));
      lastUploadAttemptMs = deps.now();
      try {
        const result = await deps.uploadPoints(attendanceId, batch);
        await queue.remove(sent);
        failures = 0;
        nextAttemptMs = 0;
        emit({ lastUploadAt: new Date(deps.now()).toISOString(), offline: false });
        if (!result.active && record && !record.drainOnly) {
          // Checked out elsewhere (e.g. kiosk) or expired: stop recording, keep draining.
          record = { ...record, drainOnly: true };
          await persistRecord();
          stopSensors();
          subscribeLifecycle();
          emit({ state: 'idle', background: false });
        }
      } catch (caught) {
        const error = normalizeApiError(caught);
        if (error.code && TERMINAL_CODES.has(error.code)) {
          stopSensors();
          await clearRecord();
          status = {
            ...IDLE,
            state: error.code === 'LOCATION_TRACKING_UNAVAILABLE' ? 'unavailable' : 'idle',
          };
          emit({});
          return;
        }
        if (error.statusCode === 400) {
          // A malformed batch can never succeed; drop it so the queue keeps moving.
          await queue.remove(sent);
          continue;
        }
        failures += 1;
        nextAttemptMs =
          deps.now() +
          Math.min(BACKOFF_BASE_MS * 2 ** (failures - 1), settings.uploadIntervalSeconds * 1000);
        emit({ offline: true });
        return;
      }
    }
    if (record?.drainOnly && !queue?.size()) {
      unsubscribers.forEach(unsubscribe => unsubscribe());
      unsubscribers = [];
      await clearRecord();
      status = { ...IDLE };
      emit({});
    }
  }

  function flush(force: boolean): Promise<void> {
    if (flushing) {
      // Re-check once the current upload finishes so no request is lost.
      rerun = rerun === 'force' || force ? 'force' : 'normal';
      return flushing;
    }
    flushing = (async () => {
      let next: boolean | null = force;
      while (next !== null) {
        rerun = null;
        await uploadLoop(next);
        next = rerun === null ? null : rerun === 'force';
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        flushing = null;
      });
    return flushing;
  }

  async function beginDrain(active: ActiveRecord) {
    record = active;
    settings = active.settings;
    queue = await openQueue(active.attendanceId);
    if (!settings) {
      await clearRecord();
      return;
    }
    subscribeLifecycle();
    await flush(true);
  }

  async function savedRecord(): Promise<ActiveRecord | null> {
    try {
      const raw = await deps.storage.getItem(ACTIVE_KEY);
      const parsed = raw ? (JSON.parse(raw) as ActiveRecord) : null;
      return parsed && Number.isInteger(parsed.attendanceId) ? parsed : null;
    } catch {
      return null;
    }
  }

  async function startTracking(
    attendanceId: number,
    options: {
      userId?: string | null;
      startFix?: { latitude: number; longitude: number; accuracyMeters: number | null } | null;
    },
  ): Promise<TrackerStatus> {
    if (
      record?.attendanceId === attendanceId &&
      !record.drainOnly &&
      ['starting', 'tracking'].includes(status.state)
    ) {
      return status;
    }
    if (record && record.attendanceId !== attendanceId) await tracker.stop();
    lastRequest = { attendanceId, userId: options.userId ?? null };
    const current = ++generation;
    emit({ state: 'starting', attendanceId, error: null });
    let session: TrackingSessionState | null = null;
    try {
      session = await deps.startSession(attendanceId, options.startFix ?? null);
    } catch (caught) {
      const error = normalizeApiError(caught);
      if (current !== generation) return status;
      if (error.code === 'LOCATION_TRACKING_UNAVAILABLE' || error.statusCode === 404) {
        status = {
          ...IDLE,
          state: error.code === 'LOCATION_TRACKING_UNAVAILABLE' ? 'unavailable' : 'idle',
        };
        emit({});
        return status;
      }
      // Offline: continue with the settings saved when this session first started.
      const saved = await savedRecord();
      if (saved?.attendanceId !== attendanceId || !saved.settings) {
        emit({ state: 'error', error: START_FAILED, offline: !error.statusCode });
        return status;
      }
      session = {
        eligible: true,
        active: true,
        attendanceId,
        attendanceSource: null,
        session: null,
        settings: saved.settings,
      };
    }
    if (current !== generation) return status;
    if (!session.eligible || !session.active || !session.settings.enabled) {
      const saved = await savedRecord();
      if (saved?.attendanceId === attendanceId) {
        await beginDrain({ ...saved, drainOnly: true });
      }
      if (current === generation && (!record || record.drainOnly)) {
        status = { ...IDLE };
        emit({});
      }
      return status;
    }
    settings = session.settings;
    record = { attendanceId, userId: options.userId ?? null, settings };
    queue = await openQueue(attendanceId);
    lastRecorded = null;
    failures = 0;
    nextAttemptMs = 0;
    await persistRecord();
    if (current !== generation) return status;
    subscribeLifecycle();
    try {
      await startSensors(current);
    } catch {
      if (current === generation) fail('Location could not be started on this device. Try again.');
    }
    return status;
  }

  const tracker = {
    getStatus: () => status,

    subscribe(listener: (next: TrackerStatus) => void) {
      listeners.add(listener);
      listener(status);
      return () => {
        listeners.delete(listener);
      };
    },

    /** Most recent recorded fix, used for the check-out location when fresh. */
    latestFix: (): GeoFix | null => lastRecorded,

    /**
     * Starts tracking for this attendance. Safe to call repeatedly: a running
     * session for the same attendance is left untouched.
     */
    start(
      attendanceId: number,
      options: {
        userId?: string | null;
        startFix?: { latitude: number; longitude: number; accuracyMeters: number | null } | null;
      } = {},
    ): Promise<TrackerStatus> {
      // A screen refresh and the app bootstrap can ask at the same moment; share one start.
      if (pendingStart?.attendanceId === attendanceId) return pendingStart.promise;
      const promise = startTracking(attendanceId, options).finally(() => {
        if (pendingStart?.promise === promise) pendingStart = null;
      });
      pendingStart = { attendanceId, promise };
      return promise;
    },

    /** Tries again after a permission, location-services or start failure. */
    async retry(): Promise<TrackerStatus> {
      if (record && !record.drainOnly && settings) {
        emit({ state: 'starting', error: null });
        try {
          await startSensors(generation);
        } catch {
          fail('Location could not be started on this device. Try again.');
        }
        return status;
      }
      if (lastRequest) return tracker.start(lastRequest.attendanceId, { userId: lastRequest.userId });
      return status;
    },

    /** Stops recording immediately; queued points keep uploading until confirmed. */
    async stop(): Promise<void> {
      generation += 1;
      lastRequest = null;
      if (!record) {
        stopSensors();
        status = { ...IDLE };
        emit({});
        return;
      }
      stopSensors();
      await flush(true);
      await finishSession();
    },

    /** Resumes after an app restart for the same signed-in user. */
    async resume(userId: string | null): Promise<void> {
      const saved = await savedRecord();
      if (!saved) return;
      if (saved.userId && userId && saved.userId !== userId) {
        record = saved;
        queue = await openQueue(saved.attendanceId);
        await clearRecord();
        return;
      }
      if (saved.drainOnly) {
        await beginDrain(saved);
        return;
      }
      await tracker.start(saved.attendanceId, { userId });
    },

    savedRecord,
  };
  return tracker;
}

export type LocationTracker = ReturnType<typeof createLocationTracker>;

let singleton: LocationTracker | null = null;

/** The app-wide tracker; created on first use so tests can inject their own. */
export function getLocationTracker(): LocationTracker {
  if (!singleton) {
    singleton = createLocationTracker({
      platform: Platform.OS,
      source: getLocationSource,
      startSession: startLocationTracking,
      uploadPoints: uploadTrackingPoints,
      storage: AsyncStorage,
      onNetworkRestored: listener => {
        let connected = true;
        return NetInfo.addEventListener(state => {
          const now = state.isConnected !== false && state.isInternetReachable !== false;
          if (now && !connected) listener();
          connected = now;
        });
      },
      onAppStateChange: listener => {
        const subscription = AppState.addEventListener('change', listener);
        return () => subscription.remove();
      },
      currentAppState: () => AppState.currentState ?? 'active',
      startBackground: () =>
        startLocationForegroundService(
          'Client visit tracking',
          'Your location is shared with your organization until you check out.',
        ),
      stopBackground: stopLocationForegroundService,
      now: () => Date.now(),
    });
  }
  return singleton;
}

/**
 * Fresh fix for check-in/check-out. Resolves null — never hangs — when
 * permission, location services or GPS are unavailable within the time limits.
 */
export async function captureTrackingFix(
  timeoutMs: number,
  maxAgeMs = 0,
  options: { highAccuracy?: boolean; permissionTimeoutMs?: number; source?: LocationSource } = {},
): Promise<GeoFix | null> {
  const source = options.source ?? getLocationSource();
  try {
    const permission = await withTimeout(
      source.requestPermission(),
      options.permissionTimeoutMs ?? 30000,
    );
    if (permission !== 'granted') return null;
    const position = await source.currentPosition({
      timeoutMs,
      maximumAgeMs: maxAgeMs,
      highAccuracy: options.highAccuracy ?? true,
    });
    return toGeoFix(position);
  } catch {
    return null;
  }
}
